import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Truck, X, Zap, ShieldCheck, DollarSign, Loader } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function BecomeDriverButton() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState(user?.phone || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  if (!user || user.role !== "customer") return null;

  const submit = async () => {
    setBusy(true);
    setErr("");
    try {
      await api.post("/user/become-driver", { phone });
      if (refresh) await refresh();
      setOpen(false);
      nav("/driver");
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="become-driver-btn"
        className="hidden sm:inline-flex items-center gap-2 px-3 py-2 bg-asap-orange text-white border-2 border-asap-orange font-bold uppercase text-xs tracking-wider hover:translate-x-[1px] hover:translate-y-[1px] transition"
      >
        <Truck size={14}/> Also Drive with Us
      </button>

      {open && (
        <div className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="become-driver-modal" onClick={(e) => e.target === e.currentTarget && setOpen(false)}>
          <div className="bg-white border-2 border-asap-deep max-w-md w-full shadow-hard">
            <div className="p-5 border-b-2 border-asap-deep bg-asap-orange text-white flex justify-between items-center">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-asap-yellow">Become a Rescuer</div>
                <h3 className="font-display text-2xl uppercase">Drive with ASAP</h3>
              </div>
              <button onClick={() => setOpen(false)} data-testid="become-driver-close" className="p-1 border-2 border-white hover:bg-white/10"><X size={16}/></button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-slate-700">Convert your customer account into a driver account — same email, same password, keeps your history. You'll be able to <strong>request AND provide</strong> roadside help.</p>
              <div className="space-y-2">
                <Perk icon={DollarSign} text="Earn 70% of every job you complete + 100% of tips"/>
                <Perk icon={Zap} text="Instant same-day payouts via Stripe (once verified)"/>
                <Perk icon={ShieldCheck} text="Founding driver spots are still free — no monthly fee"/>
              </div>
              <label className="block">
                <div className="text-xs tracking-widest uppercase font-bold text-asap-deep mb-1">Phone (for dispatch)</div>
                <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1 555-000-0000" data-testid="become-driver-phone" className="w-full px-4 py-3 border-2 border-asap-deep focus:border-asap-orange outline-none"/>
              </label>
              <div className="p-3 bg-amber-50 border-2 border-amber-400 text-xs text-amber-900">
                <strong>Next step:</strong> after conversion, upload your driver's license + insurance + vehicle registration in the driver dashboard. An admin approves you before you can go online.
              </div>
              {err && <div className="text-asap-red font-bold text-sm" data-testid="become-driver-error">{err}</div>}
              <button onClick={submit} disabled={busy} data-testid="become-driver-submit" className="w-full px-6 py-3 bg-asap-yellow text-asap-deep font-display uppercase text-sm border-2 border-asap-deep shadow-hard hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-hard-sm transition-all disabled:opacity-60 inline-flex items-center justify-center gap-2">
                {busy ? <Loader size={14} className="animate-spin"/> : <Truck size={14}/>} {busy ? "Converting…" : "Convert to Driver Account"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Perk({ icon: Icon, text }) {
  return (
    <div className="flex items-start gap-2 text-sm text-slate-700">
      <div className="w-6 h-6 rounded-full bg-asap-yellow text-asap-deep flex items-center justify-center shrink-0"><Icon size={12} strokeWidth={2.5}/></div>
      <span>{text}</span>
    </div>
  );
}
